import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Copy, RefreshCw, Trash2, Send, ChevronDown, ChevronUp, CheckCheck, ExternalLink } from "lucide-react";

export default function EmailsPage({ user }) {
  const [emails, setEmails] = useState([]);
  const [linksByEmail, setLinksByEmail] = useState({});
  const [subject, setSubject] = useState("");
  const [sender, setSender] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(user.inbound_token || "");
  const [expanded, setExpanded] = useState(null);
  const [showMailgun, setShowMailgun] = useState(false);

  const load = async () => {
    const [e, l] = await Promise.all([api.get("/emails"), api.get("/links")]);
    setEmails(e.data);
    const bucket = {};
    l.data.forEach((lk) => {
      if (lk.source_email_id) {
        bucket[lk.source_email_id] = bucket[lk.source_email_id] || [];
        bucket[lk.source_email_id].push(lk);
      }
    });
    setLinksByEmail(bucket);
  };
  useEffect(() => { load(); }, []);

  const webhookUrl = `${API}/emails/inbound?token=${token}&secret=YOUR_INBOUND_SECRET`;
  const mailgunUrl = `${API}/emails/mailgun?token=${token}&secret=YOUR_INBOUND_SECRET`;

  const rotate = async () => {
    const r = await api.post("/settings/rotate-inbound-token");
    setToken(r.data.inbound_token);
    toast.success("New inbound token generated");
  };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const ingest = async (e) => {
    e.preventDefault();
    if (!text.trim() && !subject.trim()) { toast.error("Paste some email content first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/emails/manual", { subject, sender, text });
      toast.success(`Extracted ${r.data.links_extracted} link${r.data.links_extracted === 1 ? "" : "s"}`);
      setSubject(""); setSender(""); setText("");
      load();
    } catch { toast.error("Ingest failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/emails/${id}`); load(); };

  const approveAllInEmail = async (emailId) => {
    const r = await api.post("/links/bulk-approve", { email_id: emailId });
    toast.success(`Published ${r.data.approved} link${r.data.approved === 1 ? "" : "s"}`);
    load();
  };

  return (
    <div className="p-10 max-w-6xl">
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Intake</div>
        <h1 className="font-heading font-light text-4xl tracking-tighter mt-2">Emails</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <section className="border border-zinc-200 bg-white p-6">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Forwarding webhook (generic JSON)</div>
          <div className="bg-zinc-950 text-zinc-100 p-4 font-mono text-xs break-all rounded-sm">{webhookUrl}</div>
          <div className="flex gap-2 mt-3">
            <button data-testid="copy-webhook-btn" onClick={() => copy(webhookUrl)} className="text-xs inline-flex items-center gap-2 px-3 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-sm"><Copy className="w-3 h-3" /> Copy URL</button>
            <button data-testid="rotate-token-btn" onClick={rotate} className="text-xs inline-flex items-center gap-2 px-3 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-sm"><RefreshCw className="w-3 h-3" /> Rotate token</button>
          </div>
          <button
            data-testid="show-mailgun-btn"
            type="button"
            onClick={() => setShowMailgun(!showMailgun)}
            className="mt-4 text-xs text-zinc-600 hover:text-zinc-950 underline flex items-center gap-1"
          >
            {showMailgun ? "Hide" : "Show"} Mailgun setup guide
            {showMailgun ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showMailgun && (
            <div data-testid="mailgun-guide" className="mt-4 text-xs text-zinc-600 space-y-3 leading-relaxed border-t border-zinc-200 pt-4">
              <div className="font-medium text-zinc-950">Wire it up in 4 steps:</div>
              <ol className="list-decimal ml-5 space-y-2">
                <li>Create a free Mailgun account and add/verify a sending domain (you can use their sandbox to test).</li>
                <li>Under <span className="font-mono">Receiving → Routes → Create Route</span>, set filter expression: <span className="font-mono">match_recipient(".*@your-domain.com")</span></li>
                <li>Add action: <span className="font-mono">forward("{mailgunUrl}")</span> then <span className="font-mono">stop()</span></li>
                <li>Forward your merchant emails to <span className="font-mono">anything@your-domain.com</span> — links will appear here.</li>
              </ol>
              <div className="flex gap-2 mt-2">
                <button data-testid="copy-mailgun-url" onClick={() => copy(mailgunUrl)} className="text-xs inline-flex items-center gap-2 px-3 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-sm bg-white">
                  <Copy className="w-3 h-3" /> Copy Mailgun forward URL
                </button>
                <a href="https://app.mailgun.com/mg/receiving/routes" target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-2 px-3 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-sm bg-white">
                  <ExternalLink className="w-3 h-3" /> Open Mailgun Routes
                </a>
              </div>
              <div className="text-zinc-500 pt-2">Replace <span className="font-mono">YOUR_INBOUND_SECRET</span> with the value of <span className="font-mono">INBOUND_EMAIL_SECRET</span> from your backend .env.</div>
            </div>
          )}
        </section>

        <section className="border border-zinc-200 bg-white p-6">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Paste an email manually</div>
          <form onSubmit={ingest} className="space-y-3">
            <input data-testid="manual-subject" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm" />
            <input data-testid="manual-sender" placeholder="From (e.g. deals@merchant.com)" value={sender} onChange={(e) => setSender(e.target.value)} className="w-full text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm" />
            <textarea data-testid="manual-body" placeholder="Paste the email body here. All URLs will be extracted and analyzed by AI." value={text} onChange={(e) => setText(e.target.value)} rows={7} className="w-full text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm resize-y font-mono" />
            <button type="submit" data-testid="ingest-btn" disabled={busy} className="inline-flex items-center gap-2 bg-zinc-950 text-white text-sm px-4 py-2.5 hover:bg-zinc-800 disabled:opacity-50 rounded-sm">
              <Send className="w-3.5 h-3.5" /> {busy ? "Analyzing…" : "Extract & analyze"}
            </button>
          </form>
        </section>
      </div>

      <div className="border border-zinc-200 bg-white">
        <div className="px-5 py-3 border-b border-zinc-200 text-xs tracking-[0.15em] uppercase text-zinc-500">Recent emails ({emails.length})</div>
        {emails.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">No emails captured yet.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {emails.map((e) => {
              const isOpen = expanded === e.id;
              const links = linksByEmail[e.id] || [];
              const pending = links.filter((l) => !l.is_public).length;
              return (
                <div key={e.id} data-testid={`email-row-${e.id}`}>
                  <div className="px-5 py-4 flex items-center gap-4 hover:bg-zinc-50">
                    <button data-testid={`email-expand-${e.id}`} onClick={() => setExpanded(isOpen ? null : e.id)} className="text-zinc-400 hover:text-zinc-950">
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{e.subject || "(no subject)"}</div>
                      <div className="text-xs text-zinc-500 truncate">{e.sender || "unknown sender"}</div>
                    </div>
                    <div className="text-xs font-mono text-zinc-500">{e.link_count} link{e.link_count === 1 ? "" : "s"}</div>
                    {pending > 0 && (
                      <button data-testid={`email-approve-all-${e.id}`} onClick={() => approveAllInEmail(e.id)}
                        className="text-xs inline-flex items-center gap-1 bg-zinc-950 text-white px-3 py-1.5 hover:bg-zinc-800 rounded-sm">
                        <CheckCheck className="w-3 h-3" /> Approve {pending}
                      </button>
                    )}
                    <div className="text-xs text-zinc-500">{new Date(e.received_at).toLocaleDateString()}</div>
                    <button data-testid={`email-delete-${e.id}`} onClick={() => del(e.id)} className="text-zinc-400 hover:text-red-500 p-1.5">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {isOpen && (
                    <div className="px-14 pb-4 bg-zinc-50">
                      {links.length === 0 ? (
                        <div className="text-xs text-zinc-500 py-3">No links extracted.</div>
                      ) : (
                        <div className="space-y-2 pt-2">
                          {links.map((l) => (
                            <div key={l.id} className="flex items-center gap-3 text-sm bg-white px-3 py-2 border border-zinc-200 rounded-sm">
                              <div className={`w-1.5 h-6 rounded-full ${l.is_public ? "bg-emerald-500" : "bg-amber-400"}`} />
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-medium">{l.title || l.url}</div>
                                <div className="text-xs text-zinc-500 font-mono truncate">{l.url}</div>
                              </div>
                              {l.category_name && <span className="text-xs text-zinc-500">{l.category_name}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
